import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { usePostCreateTemplateMutation, usePostEditTemplateMutation } from 'apis/report-templates.api';
import { useOpen } from 'hooks/useOpen';
import { CommonError } from 'interfaces/shared.interface';
import { ErrorModal, Input, Modal } from 'shared-components';
import ModalControlButtons from 'shared-components/ModalControlButtons';
import { successNotify } from 'utils/notifications';

import { ReportTemplateFiltersBlockHandle } from './CreateReportTemplateFiltersBlock';

interface CreateTemplateModalProps {
  onCloseModal: () => void;
  isOpenModal: boolean;
  initialName?: string;
  filtersBlockRef: React.RefObject<ReportTemplateFiltersBlockHandle>;
  isEditMode?: boolean;
  templateId?: string;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  onCloseModal,
  isOpenModal,
  initialName,
  filtersBlockRef,
  isEditMode,
  templateId,
}) => {
  const [postCreateTemplate, { error: createTemplateError }] = usePostCreateTemplateMutation();
  const [isOpenErrorModal, handleOpenErrorModal, handleOnCloseErrorModal] = useOpen();
  const [name, setName] = useState(initialName || '');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const errorData = createTemplateError as CommonError;
  const errorDetails = errorData?.data?.error as { message?: string; details?: string };
  const [updateTemplate] = usePostEditTemplateMutation();

  const handleCreate = async () => {
    const payload = filtersBlockRef.current?.buildSavePayload(name.trim());
    if (!payload) {
      return;
    }

    setLoading(true);

    try {
      if (isEditMode && templateId) {
        await updateTemplate({ id: templateId, ...payload }).unwrap();
        successNotify(t('SuccessfullyUpdated'));
      } else {
        await postCreateTemplate(payload).unwrap();
        successNotify(t('SuccessfullyCreated'));
      }

      navigate('/management/report-templates');
    } catch (error) {
      handleOpenErrorModal();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ErrorModal
        errorMessage={errorDetails?.message}
        description={errorDetails?.details}
        isOpen={isOpenErrorModal}
        handleClose={handleOnCloseErrorModal}
      />
      <Modal bodyClassName="!w-auto" isOpen={isOpenModal} onClose={onCloseModal} heading={t('CreateTemplate')}>
        <div className="pt-4 mb-2">
          <Input
            inputClassName="!w-[30rem]"
            label={t('Name')}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <ModalControlButtons
            onClose={onCloseModal}
            saveButtonText={t(isEditMode ? 'Update' : 'Create')}
            disabled={loading || !name.trim()}
            onSubmit={handleCreate}
          />
        </div>
      </Modal>
    </>
  );
};

export default CreateTemplateModal;
